/**
 * "Uptime and resources" gadget (Operate): per service, since when it is up, how many times it restarted (when the
 * runner counts it), its CPU and memory now, from the services' state the Home already polls (useProjectServices). Flow
 * keeps no history of these: the gadget says so instead of drawing a graph.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { formatBytes, formatCores, formatUptime } from '../../../services/SandboxService';
import { uptimeRows } from '../../../utils/homeGadgets/operate';
import GadgetCard, { Quiet, TabLink } from './GadgetCard';
import DataTable from '../../table/DataTable';

const STATE = { running: 'Running', building: 'Starting', crashed: 'Crashed', failed: 'Build failed', stopped: 'Stopped', built: 'Built' };
const TONE = { running: 'gd-ok', crashed: 'gd-bad', failed: 'gd-bad' };

// The service's name opens it in Apps (`openService`), else plain bold text.
const uptimeColumns = (openService) => [
  {
    key: 'service', label: 'Service', cellLabel: false, value: (r) => r.service, title: (r) => r.service,
    render: (r) => (openService
      ? <button className="gd-name-link" onClick={() => openService(r.service)} title={`Open ${r.service} in Apps`} type="button">{r.service}</button>
      : <strong>{r.service}</strong>),
  },
  { key: 'state', label: 'State', width: 96, cellLabel: false, value: (r) => STATE[r.state] || r.state, render: (r) => <span className={TONE[r.state] || ''}>{STATE[r.state] || r.state}</span> },
  { key: 'since', label: 'Up for', width: 80, value: (r) => (r.since ? -new Date(r.since).getTime() : null), render: (r) => (r.since ? formatUptime(r.since) : '—') },
  { key: 'restarts', label: 'Restarts', width: 76, align: 'right', className: 'gd-num', headerClassName: 'gd-num', value: (r) => r.restarts, render: (r) => (r.restarts === null ? '—' : r.restarts) },
  { key: 'cpu', label: 'CPU', width: 80, align: 'right', className: 'gd-num', headerClassName: 'gd-num', value: (r) => r.cpu, render: (r) => formatCores(r.cpu) },
  { key: 'memory', label: 'Memory', width: 84, align: 'right', className: 'gd-num', headerClassName: 'gd-num', value: (r) => r.memory, render: (r) => formatBytes(r.memory) },
];

export default function UptimeGadget({ items = [], loaded = true, onOpenApps = null, onOpenService = null }) {
  const rows = uptimeRows(items);
  return (
    <GadgetCard links={<TabLink onClick={onOpenApps}>Apps</TabLink>} meta="now" testId="uptime-gadget" title="Uptime and resources">
      {rows.length === 0 ? (
        <Quiet testId="gadget-empty">{loaded ? 'No service yet.' : 'Reading the services…'}</Quiet>
      ) : (
        <>
          {/* Sorts on every column; no Columns ▾ in a gadget (Apps has the full table). Narrow: no minimum width, so on a
              phone the rows stack (Gadgets.css) instead of scrolling sideways. */}
          <DataTable
            className="gd-table"
            columnsMenu={false}
            columns={uptimeColumns(onOpenService)}
            minWidth={0}
            rowKey="service"
            rowProps={(r) => ({ 'data-testid': `uptime-${r.service}` })}
            rows={rows}
          />
          <p className="gd-note">The values of now only: Flow does not keep a history of them yet.</p>
        </>
      )}
    </GadgetCard>
  );
}

UptimeGadget.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ serviceName: PropTypes.string, service: PropTypes.object })),
  loaded: PropTypes.bool,
  onOpenApps: PropTypes.func,
  // (service) => void: Apps on that service.
  onOpenService: PropTypes.func,
};
